import React, { useState, useRef, useEffect } from 'react';
import { formatFileSize, downloadFile, formatDuration, generateMockWaveform, getWaveformHeights } from '../../utils/mediaHelpers';

const VoiceMessage = ({ media, caption, isSender, category }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [waveformData, setWaveformData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [audioError, setAudioError] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    
    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => {
      setDuration(audio.duration);
      // Generate mock waveform data based on duration
      const mockWaveform = generateMockWaveform(audio.duration, 40);
      setWaveformData(mockWaveform);
      setIsLoading(false);
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const handleError = () => {
      setIsLoading(false);
      setAudioError(true);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    
    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, []);

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const seekTime = percentage * duration;
    
    audioRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const handleDownload = () => {
    downloadFile(media.url, media.originalName);
  };

  const formatTime = (time) => {
    return formatDuration(time);
  };

  const calculateProgress = () => {
    return duration > 0 ? (currentTime / duration) * 100 : 0;
  };

  // Get waveform heights with active state
  const waveformHeights = getWaveformHeights(waveformData, currentTime, duration);

  return (
    <div className={`media-message voice-message ${isSender ? 'sender' : 'receiver'}`}>
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={media.url}
        preload="metadata"
      />
      
      <div className="media-content">
        <div className="voice-player">
          {/* Play/Pause button */}
          <button 
            className={`voice-play-btn ${isPlaying ? 'playing' : ''}`}
            onClick={togglePlay}
            disabled={isLoading || audioError}
          >
            {isLoading ? (
              <div className="voice-loading-spinner"></div>
            ) : audioError ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            ) : isPlaying ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="4" width="4" height="16"/>
                <rect x="14" y="4" width="4" height="16"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
            )}
          </button>

          {/* Waveform visualization */}
          <div className="voice-waveform-container" onClick={handleSeek}>
            <div className="voice-waveform">
              {waveformHeights.map((bar, index) => (
                <div
                  key={index}
                  className={`waveform-bar ${bar.isActive ? 'active' : ''}`}
                  style={{
                    height: `${bar.height * 100}%`,
                    animationDelay: `${index * 0.05}s`
                  }}
                />
              ))}
            </div>
            <div className="voice-progress-overlay" style={{ width: `${calculateProgress()}%` }} />
          </div>

          {/* Time display */}
          <div className="voice-time">
            <span>{formatTime(currentTime)}</span>
            <span>/</span>
            <span>{formatTime(duration)}</span>
          </div>

          {/* Download button */}
          <button 
            className="voice-download-btn"
            onClick={handleDownload}
            title="Download voice note"
            disabled={isLoading || audioError}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
            </svg>
          </button>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="voice-loading">
            <div className="loading-spinner"></div>
            <span>Loading voice note...</span>
          </div>
        )}

        {/* Error state */}
        {audioError && (
          <div className="voice-error">
            <div className="error-icon">❌</div>
            <span>Failed to load voice note</span>
          </div>
        )}
      </div>

      {/* Metadata - timestamp only */}
      <div className="media-info">
        <div className="media-metadata">
          {media.size > 0 && (
            <span className="file-size">{formatFileSize(media.size)}</span>
          )}
        </div>
        <div className="message-time">
          {new Date().toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit' 
          })}
        </div>
      </div>
    </div>
  );
};

export default VoiceMessage;
